import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import DesignSystemButton from "../../components/DesignSystem/DesignSystemButton";
import {
  TextLink,
  TextSecondary,
} from "../../components/DesignSystem/DesignSystemText";
import DesignSystemTooltip from "../../components/DesignSystem/DesignSystemTooltip";
import { LayoutRow } from "../../components/DesignSystem/Layout/LayoutRow";
import {
  addEndorsement,
  IKudosEndorser,
  removeEndorsement,
} from "../../services/apis/KudosApi/KudosDetails";
import { useJwtAuth } from "../../services/auth/JwtAuthState";
import { useWallet } from "../../services/wallet/useWallet";
import { truncateAddress } from "../../utils/string";
import { useLoginScreen } from "../Account/LoginScreenState";

const KudosEndorsements: React.FC<{
  tokenId: string;
  endorsers: IKudosEndorser[] | undefined;
}> = ({ tokenId, endorsers }) => {
  const { disconnect, avatarUrl, addresses, username, displayName } =
    useWallet();
  const address = addresses[0];
  const { isAuthenticated } = useJwtAuth();
  const loginScreen = useLoginScreen();
  const endorserUsernames =
    endorsers?.map((endorser) => endorser.username) || [];

  // Keep track of whether logged-in user has endorsed token
  const [hasEndorsement, setHasEndorsement] = useState(
    endorsers
      ? endorserUsernames.includes(username) ||
          endorserUsernames.includes(displayName)
      : false,
  );

  const [endorserDisplayList, setEndorserDisplayList] =
    useState(endorserUsernames);
  const [toggleEndorse, setToggleEndorse] = useState(false);

  useEffect(() => {
    if (hasEndorsement) {
      // Set first item in list to "you" if logged-in user has endorsed this kudos
      setEndorserDisplayList([
        username,
        ...endorserUsernames.filter((endorser) => {
          return endorser != username && endorser != displayName;
        }),
      ]);
    } else {
      setEndorserDisplayList(endorserUsernames);
    }
  }, [endorsers, hasEndorsement, address]);

  useEffect(() => {
    if (
      endorserUsernames.includes(username) ||
      endorserUsernames.includes(displayName)
    ) {
      setHasEndorsement(true);
    } else if (isAuthenticated && address && !hasEndorsement && toggleEndorse) {
      toggleEndorsement();
      setToggleEndorse(false);
    }
  }, [isAuthenticated, address]);

  const toggleEndorsement = async () => {
    if (!isAuthenticated) {
      setToggleEndorse(true);
      loginScreen.open();
      return;
    }
    if (hasEndorsement) {
      await removeEndorsement(tokenId);
      // We modify the list here instead of making another call to get updated endorsers for better performance. Call fetchKudosDetails only if total real-time counts required.
      const indexOfLoggedInEndorser = Math.max(
        endorserUsernames.indexOf(username),
        endorserUsernames.indexOf(displayName),
      );
      if (indexOfLoggedInEndorser >= 0) {
        endorserUsernames.splice(indexOfLoggedInEndorser, 1);
      }
      setHasEndorsement(false);
    } else {
      await addEndorsement(tokenId);
      endorserUsernames.unshift(username);
      setHasEndorsement(true);
    }
  };

  const getEndorsersText = () => {
    const formatAddress = (endorserUsername: string) => {
      const getTextLink = (
        profileLinkAddress: string,
        displayAddress: string,
      ) => {
        return (
          <TextLink href={"/profile/" + profileLinkAddress} target="_blank">
            {displayAddress}
          </TextLink>
        );
      };
      if (endorserUsername == displayName || endorserUsername == username)
        return getTextLink(username, "you");
      if (endorserUsername.includes(".eth"))
        getTextLink(endorserUsername, endorserUsername);
      return getTextLink(
        endorserUsername,
        // @ts-ignore
        truncateAddress(endorserUsername ?? ""),
      );
    };
    if (endorserDisplayList.length == 0)
      return <TextSecondary>Click to clap</TextSecondary>;
    else if (endorserDisplayList.length == 1)
      return (
        <TextSecondary>
          {formatAddress(endorserDisplayList[0])} clapped
        </TextSecondary>
      );
    else if (endorserDisplayList.length == 2)
      return (
        <TextSecondary>
          {formatAddress(endorserDisplayList[0])} and{" "}
          {formatAddress(endorserDisplayList[1])} clapped
        </TextSecondary>
      );
    else if (endorserDisplayList.length == 3)
      return (
        <TextSecondary>
          {formatAddress(endorserDisplayList[0])},{" "}
          {formatAddress(endorserDisplayList[1])}, and{" "}
          {formatAddress(endorserDisplayList[2])} clapped
        </TextSecondary>
      );
    else {
      return (
        <TextSecondary>
          {formatAddress(endorserDisplayList[0])},{" "}
          {formatAddress(endorserDisplayList[1])}
          ,&nbsp;
          <DesignSystemTooltip>
            {endorserDisplayList.slice(2).map((endorser) => {
              return (
                <a
                  key={endorser}
                  href={"/profile/" + endorser}
                  target="_blank"
                  rel="noreferrer"
                  className={css`
                    color: #fff;
                    display: block;
                    text-decoration: none;
                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                >
                  {endorser}
                </a>
              );
            })}
            {`and ${endorserDisplayList.length - 2} others clapped`}
          </DesignSystemTooltip>
        </TextSecondary>
      );
    }
  };

  return (
    <LayoutRow
      gap="9rem"
      className={css`
        overflow: visible;
      `}
      alignY="center"
    >
      <DesignSystemButton
        className={css`
          &:active,
          &.active {
            opacity: 1;
            background: #e8e8e8;
            border-color: #e8e8e8;
          }
          box-shadow: none;
          padding: 0em;
        `}
        theme={[
          hasEndorsement
            ? "border-box linear-gradient(45deg, #b9e5cd, #b7d4f7 80%, #8E57D6)"
            : "#f4f4f4",
          "white",
        ]}
        circular
        onClick={() => toggleEndorsement()}
      >
        👏
      </DesignSystemButton>
      {endorsers && getEndorsersText()}
    </LayoutRow>
  );
};

export default KudosEndorsements;
